import { useEffect, useRef } from 'react';

import { useCaseChecksQueryLazyQuery } from '@dotfile/shared/data-access-client-portal';

import { useCase } from '../../../../shared';

/**
 * Calculates the polling interval based on the number of attempts made.
 * Uses an adaptive polling strategy with increasing intervals to reduce server load
 * starting with every seconds since most pipelines are short and switching to an
 * exponential backoff capped at 16s.
 *
 * @param attemptCount - The number of polling attempts that have been made
 * @returns The time in milliseconds to wait before the next poll
 */
const getNextPollInterval = (attemptCount: number): number => {
  if (attemptCount < 4) {
    return 1000; // Every second for the first 4 polls
  }

  // Then use exponential backoff based on 2s capped at 16s
  return Math.min(Math.pow(2, attemptCount - 3), 16) * 1000;
};

export const usePollCaseChecksAutomation = () => {
  const attemptCountRef = useRef(0);

  const [fetchCaseChecksQuery, caseChecksQuery] = useCaseChecksQueryLazyQuery();

  const { refetch: refetchCase, ...caseQuery } = useCase();

  const caseId = caseQuery.data?.case?.id;
  const hasOngoingPipelineWithClientPortalStages =
    caseQuery.data?.case?.automation.hasOngoingPipelineWithClientPortalStages;

  useEffect(() => {
    // Wait for the case to load
    if (!caseId) {
      return;
    }

    // Fetch case checks if no automation is running
    if (!hasOngoingPipelineWithClientPortalStages) {
      fetchCaseChecksQuery({ variables: { id: caseId } });
      return;
    }

    // Poll the case to check if the automation is still running
    let timeout: NodeJS.Timeout;

    const timeoutFn = async () => {
      const res = await refetchCase();
      if (res.data?.case?.automation.hasOngoingPipelineWithClientPortalStages) {
        attemptCountRef.current++;

        timeout = setTimeout(
          timeoutFn,
          getNextPollInterval(attemptCountRef.current),
        );
      }
    };

    timeout = setTimeout(
      timeoutFn,
      getNextPollInterval(attemptCountRef.current),
    );

    return () => clearTimeout(timeout);
  }, [
    caseId,
    hasOngoingPipelineWithClientPortalStages,
    refetchCase,
    fetchCaseChecksQuery,
  ]);

  return {
    loading: caseChecksQuery.loading || !caseChecksQuery.called,
    error: caseChecksQuery.error || caseQuery.error,
    data: caseChecksQuery.data,
    hasOngoingPipelineWithClientPortalStages,
  };
};
